import {ITrackEventName, ITrackEventParams} from '@wix/native-components-infra';
import {CartModel} from '../models/checkout/Cart/Cart.model';

type TrackEventTuple = [ITrackEventName, Record<string, any>];

const enum PaymentMethodType {
  PAYPAL = 'paypal',
}

export enum EventAction {
  AgreeToTerms = 'Agree To Terms',
  SuccessCoupon = 'success_coupon',
  FailedCoupon = 'failed_coupon',
  PlaceOrderFailed = 'place_order_failed',
}

const origin = 'Stores';
const eventCategory = 'Enhanced Ecommerce - Stores';

export const lineItemsForAnalytics = (cart: CartModel) =>
  cart.lineItems.map((item) => ({
    id: item.source.catalogReference?.catalogItemId,
    name: item.name,
    category: 'All Products',
    price: item.pricing.totalPrice?.amount,
    currency: cart.paymentInfo.currencyCode,
    quantity: item.quantityInfo.quantity,
  }));

const analyticsEventBase = (action: EventAction, cart?: CartModel) => ({
  eventCategory,
  eventLabel: origin,
  action,
  ...(cart !== undefined
    ? {
        currency: cart.paymentInfo.currencyCode!,
        cartId: cart.id,
        contents: lineItemsForAnalytics(cart),
      }
    : {}),
});

export const FastFlowAnalyticsEventParams = ['AddPaymentInfo', {option: PaymentMethodType.PAYPAL, origin}] as [
  ITrackEventName,
  ITrackEventParams
];

export const AgreeToTermsAnalyticsEventParams = [
  'CustomEvent',
  analyticsEventBase(EventAction.AgreeToTerms),
] as TrackEventTuple;

export const StartPayment = ['StartPayment' as ITrackEventName, {origin}] as TrackEventTuple;

export const AddPaymentInfo = ({activePaymentMethod}: {activePaymentMethod?: string}) =>
  [
    'AddPaymentInfo' as ITrackEventName,
    {
      origin,
      option: activePaymentMethod,
    },
  ] as TrackEventTuple;

export const trackInitiateCheckout = ({cart}: {cart: CartModel}) => {
  return [
    'InitiateCheckout' as ITrackEventName,
    {
      contents: lineItemsForAnalytics(cart),
      origin,
    },
  ] as TrackEventTuple;
};

export const trackApplyCouponSuccess = ({cart, couponCode}: {cart: CartModel; couponCode: string}) => {
  return [
    EventAction.SuccessCoupon as unknown as ITrackEventName,
    {
      origin,
      coupon: couponCode,
      ...analyticsEventBase(EventAction.SuccessCoupon, cart),
    },
  ] as TrackEventTuple;
};

export const trackApplyCouponFailed = ({
  cart,
  couponCode,
  errorCode,
}: {
  cart: CartModel;
  couponCode: string;
  errorCode: string;
}) => {
  return [
    EventAction.FailedCoupon as unknown as ITrackEventName,
    {
      origin,
      coupon: couponCode,
      ...analyticsEventBase(EventAction.FailedCoupon, cart),
      error_code: errorCode,
    },
  ] as TrackEventTuple;
};

export const trackPlaceOrderFailedFromCheckout = ({
  errorCode,
  cart,
}: {
  errorCode: string;
  cart: CartModel;
}): TrackEventTuple => {
  return [
    EventAction.PlaceOrderFailed as unknown as ITrackEventName,
    {
      origin,
      ...analyticsEventBase(EventAction.PlaceOrderFailed, cart),
      failure_source: 'checkout' as const,
      error_code: errorCode,
    },
  ] as TrackEventTuple;
};

export const trackPlaceOrderFailedFromPayment = ({
  paymentStatus,
  failureDetails,
  cart,
}: {
  paymentStatus: string;
  failureDetails?: string;
  cart: CartModel;
}): TrackEventTuple => {
  return [
    EventAction.PlaceOrderFailed as unknown as ITrackEventName,
    {
      origin,
      ...analyticsEventBase(EventAction.PlaceOrderFailed, cart),
      failure_source: 'payment' as const,
      payment_status: paymentStatus,
      failure_details: failureDetails,
    },
  ] as TrackEventTuple;
};
